import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { mentorshipApi, showError } from "../lib";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import {
  Loader,
  Send,
  Search,
  Phone,
  Video,
  MoreVertical,
  Check,
  CheckCheck,
  ArrowLeft,
  User,
  MessageCircle,
  Info,
  Flag,
} from "lucide-react";

interface Conversation {
  user: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    isOnline?: boolean;
    lastSeen?: string;
  };
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  isTyping?: boolean;
}

interface Message {
  _id: string;
  sender: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  content: string;
  isRead: boolean;
  createdAt: string;
  status?: "sending" | "sent" | "delivered" | "read";
}

const MentorshipMessagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    socket,
    isConnected,
    onlineUsers,
    joinRoom,
    leaveRoom,
    on,
    off,
    sendTyping: emitTyping,
  } = useSocket();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [showChatMenu, setShowChatMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setShowSidebar(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser);
      if (isMobile) {
        setShowSidebar(false);
      }
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!socket || !user?._id || !isConnected) return;
    const roomId = `user:${user._id}`;
    joinRoom(roomId);
    return () => {
      leaveRoom(roomId);
    };
  }, [socket, user?._id, isConnected, joinRoom, leaveRoom]);

  useEffect(() => {
    if (!socket || !selectedUser || !isConnected) return;

    const roomId = [user?._id, selectedUser].sort().join("_");
    joinRoom(roomId);

    const handleNewMessage = (msg: any) => {
      if (msg.sender?._id === selectedUser || msg.sender?._id === user?._id) {
        setMessages((prev) => {
          if (msg.sender?._id === user?._id) {
            const tempMsgIndex = prev.findIndex(
              (m) => m._id.startsWith("temp-") && m.content === msg.content,
            );
            if (tempMsgIndex !== -1) {
              const newMessages = [...prev];
              newMessages[tempMsgIndex] = {
                ...msg,
                isRead: msg.sender?._id === user?._id,
              };
              return newMessages;
            }
          }
          if (prev.find((m) => m._id === msg._id)) return prev;
          return [...prev, { ...msg, isRead: msg.sender?._id === user?._id }];
        });
      }
    };

    const handleTyping = (data: {
      userId: string;
      username: string;
      isTyping: boolean;
    }) => {
      if (data.userId === selectedUser) {
        setTypingUser(data.isTyping ? data.username : null);
      }
    };

    on("new_message", handleNewMessage);
    on("user_typing", handleTyping);

    return () => {
      leaveRoom(roomId);
      off("new_message", handleNewMessage);
      off("user_typing", handleTyping);
    };
  }, [
    socket,
    selectedUser,
    isConnected,
    user?._id,
    joinRoom,
    leaveRoom,
    on,
    off,
  ]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendTyping = (isTyping: boolean) => {
    if (isConnected && selectedUser) {
      const roomId = [user?._id, selectedUser].sort().join("_");
      emitTyping({ roomId, isTyping });
    }
  };

  const handleTypingInput = () => {
    sendTyping(true);
    if (typingTimeout) clearTimeout(typingTimeout);
    const timeout = setTimeout(() => {
      sendTyping(false);
    }, 2000);
    setTypingTimeout(timeout);
  };

  useEffect(() => {
    setConversations((prev) =>
      prev.map((conv) => ({
        ...conv,
        user: {
          ...conv.user,
          isOnline: onlineUsers.includes(String(conv.user._id)),
        },
      })),
    );
  }, [onlineUsers]);

  // Helper function to get user display name
  const getUserDisplayName = (userObj: any) => {
    if (!userObj) return "User";
    if (userObj.name) return userObj.name;
    if (userObj.firstName && userObj.lastName) {
      return `${userObj.firstName} ${userObj.lastName}`;
    }
    if (userObj.firstName) return userObj.firstName;
    if (userObj.lastName) return userObj.lastName;
    return "User";
  };

  const fetchConversations = async () => {
    try {
      const response = await mentorshipApi.getConversations();
      if (response.success) {
        // FIX: Use response.conversations instead of response.data
        // The backend returns { conversations: [...], pagination: {...} }
        const conversationsData = response.conversations || response.data || [];
        
        console.log("Fetched conversations:", conversationsData); // Debug log
        
        const conversationsWithStatus = conversationsData.map((conv: any) => {
          const userObj = conv.user || {};
          const displayName = getUserDisplayName(userObj);
          
          return {
            ...conv,
            user: {
              ...userObj,
              name: displayName || userObj.name || "User",
              isOnline: onlineUsers.includes(String(userObj._id)) || userObj.isOnline,
            },
          };
        });
        
        // Remove any filtering - show ALL conversations
        setConversations(conversationsWithStatus);
      }
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
      showError("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId: string) => {
    try {
      setLoadingMessages(true);
      const response = await mentorshipApi.getMessagesWithUser(userId, {
        limit: 50,
      });
      if (response.success) {
        // FIX: Use response.messages instead of response.data
        const messagesData = response.messages || response.data || [];
        
        // Ensure all messages have sender names
        const messagesWithNames = messagesData.map((msg: any) => ({
          ...msg,
          sender: {
            ...msg.sender,
            name: getUserDisplayName(msg.sender) || msg.sender?.name || "User",
          },
        }));
        setMessages(messagesWithNames);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      showError("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    const messageContent = newMessage.trim();
    setNewMessage("");

    const tempId = "temp-" + Date.now();
    const tempMessage: Message = {
      _id: tempId,
      sender: {
        _id: user?._id || "",
        name: user?.username || user?.firstName || "You",
        avatar: user?.avatar,
      },
      content: messageContent,
      isRead: false,
      createdAt: new Date().toISOString(),
      status: "sent",
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await mentorshipApi.sendMessage(
        selectedUser,
        messageContent,
      );
      if (response.success && response.data) {
        const serverMsg: Message = {
          _id: response.data._id || tempId,
          sender: {
            _id: response.data.sender?._id || user?._id || "",
            name: response.data.sender?.name || user?.username || user?.firstName || "You",
            avatar: response.data.sender?.avatar || user?.avatar,
          },
          content: response.data.content || messageContent,
          isRead: response.data.isRead ?? false,
          createdAt: response.data.createdAt || new Date().toISOString(),
          status: "sent",
        };
        setMessages((prev) =>
          prev.map((m) => (m._id === tempId ? serverMsg : m)),
        );
      } else {
        setMessages((prev) => prev.filter((msg) => msg._id !== tempId));
        showError("Failed to send message");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages((prev) => prev.filter((msg) => msg._id !== tempId));
      showError("Failed to send message");
    }
  };

  const handleBackToConversations = () => {
    setSelectedUser(null);
    setShowSidebar(true);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / 86400000);

    if (days === 0) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatMessageTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Only filter by search - NO status filtering
  const filteredConversations = conversations.filter((c) =>
    c.user.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedConversation = conversations.find(
    (c) => c.user._id === selectedUser,
  );

  const renderMessageStatus = (status?: string) => {
    if (status === "sending") {
      return <Loader className="w-3 h-3 animate-spin" />;
    }
    if (status === "sent") {
      return <Check className="w-3 h-3 text-gray-400" />;
    }
    if (status === "delivered") {
      return <CheckCheck className="w-3 h-3 text-gray-400" />;
    }
    if (status === "read") {
      return <CheckCheck className="w-3 h-3 text-blue-500" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-sm text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      <div className="flex h-screen overflow-hidden">
        {/* Conversations Sidebar - Facebook Style */}
        <div
          className={`
          ${isMobile && !showSidebar ? "hidden" : "flex"}
          ${isMobile ? "w-full absolute inset-0 z-20 bg-white" : "w-96 relative"}
          flex-col h-full bg-white border-r border-gray-200
        `}
        >
          {/* Sidebar Header */}
          <div className="sticky top-0 z-10 px-4 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-10 h-10 shadow-md rounded-xl bg-gradient-to-r from-amber-600 to-orange-500">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Messages</h1>
                  <p className="text-xs text-gray-500">
                    {conversations.length} conversations
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/mentorship")}
                className="px-3 py-2 text-sm font-medium text-white transition-all rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
              >
                Find Mentor
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full py-3 pl-10 pr-4 text-sm bg-gray-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:bg-white"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-4 py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                  <MessageCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {conversations.length === 0 ? "No conversations yet" : "No conversations match your search"}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {conversations.length === 0 ? "Start a conversation with a mentor" : "Try a different search term"}
                </p>
                {conversations.length === 0 && (
                  <button
                    onClick={() => navigate("/mentorship")}
                    className="px-4 py-2 mt-4 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
                  >
                    Browse Mentors
                  </button>
                )}
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.user._id}
                  onClick={() => setSelectedUser(conv.user._id)}
                  className={`w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-all ${
                    selectedUser === conv.user._id
                      ? "bg-orange-50 border-r-4 border-orange-500"
                      : ""
                  }`}
                >
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 overflow-hidden rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
                      {conv.user.avatar ? (
                        <img
                          src={conv.user.avatar}
                          alt={conv.user.name}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <User className="w-6 h-6 text-orange-500" />
                      )}
                    </div>
                    {conv.user.isOnline && (
                      <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full ring-2 ring-white animate-pulse"></span>
                    )}
                  </div>

                   {/* Conversation Info */}
                   <div className="flex-1 min-w-0 text-left">
                     <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                         <span className="text-sm font-semibold text-gray-900 truncate">
                           {conv.user.firstName && conv.user.lastName ? `${conv.user.firstName} ${conv.user.lastName}` : conv.user.name || conv.user.email || "User"}
                         </span>
                         {conv.user.isOnline && (
                           <span className="text-[10px] font-semibold text-green-600 whitespace-nowrap">Online</span>
                         )}
                       </div>
                      {conv.user.email && (
                        <span className="text-xs text-gray-400 truncate ml-1">
                          {conv.user.email}
                        </span>
                      )}
                      <span className="ml-2 text-xs text-gray-400 shrink-0">
                        {formatTime(conv.lastMessageAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500 truncate max-w-[180px]">
                        {conv.isTyping ? (
                          <span className="text-orange-500">Typing...</span>
                        ) : (
                          truncateText(conv.lastMessage, 35)
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        {conv.sessionStatus && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            ['confirmed','completed'].includes(conv.sessionStatus)
                              ? 'bg-green-100 text-green-700'
                              : conv.sessionStatus === 'approved'
                                ? 'bg-blue-100 text-blue-700'
                                : ['pending'].includes(conv.sessionStatus)
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                          }`}>
                            {conv.sessionStatus}
                          </span>
                        )}
                        {(conv.sessionStatus === 'approved' || conv.sessionStatus === 'confirmed' || conv.sessionStatus === 'completed') && (
                          <span className="text-[10px] text-green-600 font-medium">Messageable</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div
          className={`
          ${isMobile && showSidebar ? "hidden" : "flex"}
          flex-1 flex flex-col bg-white
          ${isMobile && selectedUser ? "absolute inset-0 z-30 bg-white" : "relative"}
        `}
        >
          {!selectedUser ? (
            <div className="flex flex-col items-center justify-center flex-1 px-4">
              <div className="flex items-center justify-center w-20 h-20 mb-4 bg-gray-100 rounded-full">
                <MessageCircle className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-900">Your Messages</p>
              <p className="mt-1 text-sm text-gray-500">
                Select a conversation to start messaging
              </p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex items-center min-w-0 gap-3">
                  {isMobile && (
                    <button
                      onClick={handleBackToConversations}
                      className="p-2 -ml-2 transition rounded-lg hover:bg-gray-100 shrink-0"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                  )}

                  <div className="relative shrink-0">
                    <div className="flex items-center justify-center w-10 h-10 overflow-hidden rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
                      {selectedConversation?.user.avatar ? (
                        <img
                          src={selectedConversation.user.avatar}
                          alt=""
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <User className="w-5 h-5 text-orange-500" />
                      )}
                    </div>
                    {selectedConversation?.user.isOnline && (
                      <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full ring-2 ring-white animate-pulse"></span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 truncate">
                      {selectedConversation?.user.name}
                    </h3>
                    <p className="text-xs font-medium text-gray-500">
                      {typingUser ? (
                        <span className="text-orange-500">Typing...</span>
                      ) : selectedConversation?.user.isOnline ? (
                        <span className="text-green-600 font-semibold">● Online</span>
                      ) : (
                        "Offline"
                      )}
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <button
                    onClick={() => setShowChatMenu(!showChatMenu)}
                    className="p-2 transition rounded-lg hover:bg-gray-100 shrink-0"
                  >
                    <MoreVertical className="w-5 h-5 text-gray-600" />
                  </button>

                  {showChatMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowChatMenu(false)}
                      />
                      <div className="absolute right-0 z-20 w-48 py-1 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                        <button className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Info className="w-4 h-4" />
                          View Profile
                        </button>
                        <button className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Phone className="w-4 h-4" />
                          Voice Call
                        </button>
                        <button className="flex items-center w-full gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Video className="w-4 h-4" />
                          Video Call
                        </button>
                        <hr className="my-1" />
                        <button className="flex items-center w-full gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          <Flag className="w-4 h-4" />
                          Report
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="px-4 py-4 space-y-3">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader className="w-8 h-8 text-orange-500 animate-spin" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                      <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                        <MessageCircle className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        No messages yet
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Send a message to start the conversation
                      </p>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isMe = msg.sender._id === user?.id;
                      const showTime =
                        index === messages.length - 1 ||
                        messages[index + 1].sender._id !== msg.sender._id;

                      return (
                        <div
                          key={msg._id}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`flex max-w-[80%] ${isMe ? "flex-row-reverse" : "flex-row"} items-end gap-2`}
                          >
                            {!isMe && (
                              <div className="flex items-center justify-center w-8 h-8 overflow-hidden rounded-full bg-gradient-to-br from-amber-100 to-orange-100 shrink-0">
                                {msg.sender.avatar ? (
                                  <img
                                    src={msg.sender.avatar}
                                    alt=""
                                    className="object-cover w-full h-full"
                                  />
                                ) : (
                                  <User className="w-4 h-4 text-orange-500" />
                                )}
                              </div>
                            )}

                            <div>
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  isMe
                                    ? "bg-orange-500 text-white rounded-br-sm"
                                    : "bg-white text-gray-900 shadow-sm rounded-bl-sm border border-gray-100"
                                }`}
                              >
                                <p className="text-sm leading-relaxed break-words">
                                  {msg.content}
                                </p>
                              </div>
                              {showTime && (
                                <div
                                  className={`flex items-center gap-1 mt-1 ${isMe ? "justify-end" : "justify-start"}`}
                                >
                                  <span className="text-xs text-gray-400">
                                    {formatMessageTime(msg.createdAt)}
                                  </span>
                                  {isMe && renderMessageStatus(msg.status)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <div className="p-4 bg-white border-t border-gray-100">
                <div className="flex items-end gap-2">
                  <div className="relative flex-1">
                    <textarea
                      ref={inputRef as any}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTypingInput();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Type a message..."
                      rows={1}
                      className="w-full px-4 py-3 text-sm bg-gray-100 border-0 resize-none rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:bg-white max-h-32"
                      style={{ minHeight: "48px" }}
                    />
                  </div>

                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    className={`p-3 rounded-full transition-all shrink-0 ${
                      newMessage.trim()
                        ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-md hover:shadow-lg"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentorshipMessagesPage;